import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { asRatePercent } from "@bw/shared/types";
import type {
  OpenPlayCancellationPolicyCreate,
  OpenPlayCancellationPolicyPreviewResponse,
  OpenPlayCancellationPolicyResponse,
} from "@bw/shared/types/generated";
import { useEffect, useState } from "react";

import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  getOpenPlayCancellationPolicyConfiguration,
  previewOpenPlayCancellationPolicy,
  publishOpenPlayCancellationPolicy,
} from "@/libs/dropInApi";
import {
  fromZonedDateTimeInput,
  toZonedDateTimeInput,
} from "@/libs/zonedDateTime";

function currentDateTimeInput(timeZone: string | null): string {
  return toZonedDateTimeInput(new Date().toISOString(), timeZone);
}

function policyEffectiveLabel(
  policy: OpenPlayCancellationPolicyResponse,
  timeZone: string | null,
) {
  const effectiveAt = new Date(policy.effective_at);
  let formatted = "at the configured time";
  if (!Number.isNaN(effectiveAt.getTime()) && timeZone) {
    try {
      formatted = new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone,
      }).format(effectiveAt);
    } catch {
      // Invalid facility timezones fail the editor closed below.
    }
  }
  return `Policy version ${policy.policy_version} is effective ${formatted}.`;
}

export function OpenPlayCancellationPolicyCard({
  facilityId,
  canManage,
  permissionChecked,
}: {
  facilityId: string;
  canManage: boolean;
  permissionChecked: boolean;
}) {
  const { facility, facilityId: resolvedFacilityId } = useResolvedFacility();
  const facilityTimeZone =
    resolvedFacilityId === facilityId ? facility?.timezone || null : null;
  const [currentPolicy, setCurrentPolicy] =
    useState<OpenPlayCancellationPolicyResponse | null>(null);
  const [scheduledPolicies, setScheduledPolicies] = useState<
    OpenPlayCancellationPolicyResponse[]
  >([]);
  const [pendingCancellationEnabled, setPendingCancellationEnabled] =
    useState(false);
  const [activeWindowMinutes, setActiveWindowMinutes] = useState<number | null>(
    0,
  );
  const [cardRefundPercentage, setCardRefundPercentage] = useState<
    number | null
  >(0);
  const [restorePackageVisit, setRestorePackageVisit] = useState(false);
  const [effectiveAt, setEffectiveAt] = useState(() =>
    currentDateTimeInput(facilityTimeZone),
  );
  const [loading, setLoading] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [preview, setPreview] =
    useState<OpenPlayCancellationPolicyPreviewResponse | null>(null);
  const [previewedDraft, setPreviewedDraft] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setCurrentPolicy(null);
    setScheduledPolicies([]);
    setPreview(null);
    setPreviewedDraft(null);
    setNotice(null);
    setActionError(null);

    if (!permissionChecked || !canManage) {
      setLoading(!permissionChecked);
      return () => {
        cancelled = true;
      };
    }

    getOpenPlayCancellationPolicyConfiguration(facilityId)
      .then((configuration) => {
        if (cancelled) return;
        const policy = configuration.current_policy ?? null;
        setCurrentPolicy(policy);
        setScheduledPolicies(configuration.scheduled_policies ?? []);
        if (!policy) {
          setPendingCancellationEnabled(false);
          setActiveWindowMinutes(0);
          setCardRefundPercentage(0);
          setRestorePackageVisit(false);
          setEffectiveAt(currentDateTimeInput(facilityTimeZone));
          return;
        }
        setPendingCancellationEnabled(
          policy.pending_validation_cancellation_enabled,
        );
        setActiveWindowMinutes(policy.active_cancellation_window_minutes);
        setCardRefundPercentage(Number(policy.card_refund_percentage));
        setRestorePackageVisit(policy.restore_package_visit);
        setEffectiveAt(currentDateTimeInput(facilityTimeZone));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLoadError(
          error instanceof Error
            ? error.message
            : "Failed to load the drop-in cancellation policy",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canManage, facilityId, facilityTimeZone, loadAttempt, permissionChecked]);

  function buildDraft(): OpenPlayCancellationPolicyCreate | null {
    if (
      activeWindowMinutes == null ||
      !Number.isInteger(activeWindowMinutes) ||
      activeWindowMinutes < 0 ||
      activeWindowMinutes > 1440 ||
      cardRefundPercentage == null ||
      !Number.isFinite(cardRefundPercentage) ||
      cardRefundPercentage < 0 ||
      cardRefundPercentage > 100 ||
      !/^\d+(?:\.\d{1,2})?$/.test(String(cardRefundPercentage))
    ) {
      return null;
    }
    const parsedEffectiveAt = fromZonedDateTimeInput(
      effectiveAt,
      facilityTimeZone,
    );
    if (!parsedEffectiveAt) return null;
    const brandedRefund = asRatePercent(String(cardRefundPercentage));
    if (brandedRefund == null) return null;
    return {
      effective_at: parsedEffectiveAt,
      pending_validation_cancellation_enabled: pendingCancellationEnabled,
      active_cancellation_window_minutes: activeWindowMinutes,
      card_refund_percentage: brandedRefund,
      restore_package_visit: restorePackageVisit,
    };
  }

  const draft = buildDraft();
  const serializedDraft = draft ? JSON.stringify(draft) : null;
  const previewIsCurrent =
    serializedDraft !== null && serializedDraft === previewedDraft;
  const editorDisabled =
    loading ||
    publishing ||
    previewing ||
    !facilityTimeZone ||
    !canManage ||
    !permissionChecked;

  function invalidatePreview() {
    setPreview(null);
    setPreviewedDraft(null);
    setActionError(null);
    setNotice(null);
  }

  async function previewPolicy() {
    const nextDraft = buildDraft();
    if (!nextDraft || previewing || publishing || !canManage) return;
    setPreviewing(true);
    setActionError(null);
    setNotice(null);
    try {
      const nextPreview = await previewOpenPlayCancellationPolicy(
        facilityId,
        nextDraft,
      );
      setPreview(nextPreview);
      setPreviewedDraft(JSON.stringify(nextDraft));
    } catch (error) {
      setPreview(null);
      setPreviewedDraft(null);
      setActionError(
        error instanceof Error
          ? error.message
          : "Failed to preview the drop-in cancellation policy",
      );
    } finally {
      setPreviewing(false);
    }
  }

  async function publishPolicy() {
    const nextDraft = buildDraft();
    if (
      !nextDraft ||
      JSON.stringify(nextDraft) !== previewedDraft ||
      publishing ||
      previewing ||
      !canManage
    ) {
      return;
    }
    setPublishing(true);
    setActionError(null);
    setNotice(null);
    try {
      const published = await publishOpenPlayCancellationPolicy(
        facilityId,
        nextDraft,
      );
      setPreview(null);
      setPreviewedDraft(null);
      setNotice(`Policy version ${published.policy_version} published.`);
      try {
        const configuration =
          await getOpenPlayCancellationPolicyConfiguration(facilityId);
        setCurrentPolicy(configuration.current_policy ?? null);
        setScheduledPolicies(configuration.scheduled_policies ?? []);
      } catch {
        setActionError(
          "Policy published, but the latest policy state could not be refreshed. Reload the page.",
        );
      }
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : "Failed to publish the drop-in cancellation policy",
      );
    } finally {
      setPublishing(false);
    }
  }

  return (
    <Paper
      component="section"
      data-slot="card"
      id="open-play-cancellation-policy"
      sx={{ overflow: "hidden", scrollMarginTop: 80 }}
      variant="outlined"
    >
      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
        <Typography
          component="h2"
          id="open-play-cancellation-title"
          variant="h6"
        >
          Drop-in cancellation
        </Typography>
      </Box>
      <Divider />
      <Stack spacing={2} sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>
        <Typography color="text.secondary" variant="body2">
          Set the exact refund and visit-pass terms players accept at drop-in
          checkout. Publishing creates a new immutable version.
        </Typography>

        {permissionChecked && !canManage ? (
          <Typography color="text.secondary" variant="body2">
            Cancellation policy details and publishing are available to facility
            managers.
          </Typography>
        ) : loading || !permissionChecked ? (
          <Stack
            direction="row"
            role="status"
            spacing={1}
            sx={{ alignItems: "center" }}
          >
            <CircularProgress aria-hidden size={18} />
            <Typography color="text.secondary" variant="body2">
              Loading cancellation policy...
            </Typography>
          </Stack>
        ) : loadError ? (
          <Alert
            action={
              <Button
                color="inherit"
                onClick={() => setLoadAttempt((attempt) => attempt + 1)}
                size="small"
                type="button"
              >
                Retry
              </Button>
            }
            severity="error"
          >
            {loadError}
          </Alert>
        ) : (
          <>
            {currentPolicy ? (
              <Box
                sx={{
                  borderColor: "divider",
                  borderLeft: 2,
                  pl: 1.5,
                }}
              >
                <Typography sx={{ fontWeight: 700 }} variant="body2">
                  Current:{" "}
                  {policyEffectiveLabel(currentPolicy, facilityTimeZone)}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                  variant="caption"
                >
                  {currentPolicy.summary}
                </Typography>
              </Box>
            ) : (
              <Typography color="text.secondary" variant="body2">
                No policy is effective yet. Until one is published, players
                cannot complete drop-in checkout.
              </Typography>
            )}

            {scheduledPolicies.length > 0 ? (
              <Box
                sx={{
                  borderColor: "divider",
                  borderLeft: 2,
                  pl: 1.5,
                }}
              >
                <Typography sx={{ fontWeight: 700 }} variant="body2">
                  Upcoming policy changes
                </Typography>
                <Stack
                  component="ul"
                  divider={<Divider flexItem />}
                  sx={{ listStyle: "none", m: 0, mt: 0.5, p: 0 }}
                >
                  {scheduledPolicies.map((policy) => (
                    <Box component="li" key={policy.policy_id} sx={{ py: 1 }}>
                      <Typography variant="caption">
                        {policyEffectiveLabel(policy, facilityTimeZone)}
                      </Typography>
                      <Typography
                        color="text.secondary"
                        component="p"
                        sx={{ mt: 0.25 }}
                        variant="caption"
                      >
                        {policy.summary}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : null}

            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              }}
            >
              <TextField
                disabled={editorDisabled}
                fullWidth
                id="open-play-policy-effective-at"
                label="Effective from"
                onChange={(event) => {
                  setEffectiveAt(event.target.value);
                  invalidatePreview();
                }}
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                type="datetime-local"
                value={effectiveAt}
              />
              <TextField
                aria-invalid={draft == null || undefined}
                disabled={editorDisabled}
                fullWidth
                id="open-play-policy-active-window"
                label="Active cancellation window (minutes)"
                onChange={(event) => {
                  setActiveWindowMinutes(
                    event.target.value === ""
                      ? null
                      : Number(event.target.value),
                  );
                  invalidatePreview();
                }}
                size="small"
                slotProps={{ htmlInput: { max: 1440, min: 0, step: 1 } }}
                type="number"
                value={activeWindowMinutes ?? ""}
              />
              <TextField
                aria-invalid={draft == null || undefined}
                disabled={editorDisabled}
                fullWidth
                id="open-play-policy-card-refund"
                label="Card refund (%)"
                onChange={(event) => {
                  setCardRefundPercentage(
                    event.target.value === ""
                      ? null
                      : Number(event.target.value),
                  );
                  invalidatePreview();
                }}
                size="small"
                slotProps={{ htmlInput: { max: 100, min: 0, step: "0.01" } }}
                type="number"
                value={cardRefundPercentage ?? ""}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              }}
            >
              <Paper sx={{ px: 1.5, py: 0.5 }} variant="outlined">
                <FormControlLabel
                  control={
                    <Switch
                      checked={pendingCancellationEnabled}
                      disabled={editorDisabled}
                      id="pending-cancellation"
                      onChange={(_, checked) => {
                        setPendingCancellationEnabled(checked);
                        invalidatePreview();
                      }}
                    />
                  }
                  label="Cancel before staff validation"
                  labelPlacement="start"
                  sx={{ justifyContent: "space-between", m: 0, width: "100%" }}
                />
              </Paper>
              <Paper sx={{ px: 1.5, py: 0.5 }} variant="outlined">
                <FormControlLabel
                  control={
                    <Switch
                      checked={restorePackageVisit}
                      disabled={editorDisabled}
                      id="restore-visit-pass"
                      onChange={(_, checked) => {
                        setRestorePackageVisit(checked);
                        invalidatePreview();
                      }}
                    />
                  }
                  label="Restore visit pass"
                  labelPlacement="start"
                  sx={{ justifyContent: "space-between", m: 0, width: "100%" }}
                />
              </Paper>
            </Box>

            {draft == null ? (
              <Alert severity="error">
                Use a whole-minute window from 0 to 1440, a refund from 0% to
                100% with at most two decimals, and a valid effective time.
              </Alert>
            ) : null}

            {preview ? (
              <Box
                sx={{
                  bgcolor: "action.hover",
                  borderColor: "primary.main",
                  borderLeft: 3,
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Typography sx={{ fontWeight: 700 }} variant="body2">
                  Player checkout preview
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                  variant="body2"
                >
                  {preview.summary}
                </Typography>
              </Box>
            ) : null}
            {actionError ? <Alert severity="error">{actionError}</Alert> : null}
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
            >
              <Button
                disabled={editorDisabled || draft == null}
                onClick={() => void previewPolicy()}
                size="small"
                type="button"
                variant="outlined"
              >
                {previewing ? "Previewing..." : "Preview policy"}
              </Button>
              <Button
                disabled={editorDisabled || !previewIsCurrent}
                onClick={() => void publishPolicy()}
                size="small"
                type="button"
                variant="contained"
              >
                {publishing ? "Publishing..." : "Publish policy"}
              </Button>
              {notice ? (
                <Typography
                  color="text.secondary"
                  role="status"
                  variant="body2"
                >
                  {notice}
                </Typography>
              ) : null}
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
}
